import { ActionIcon, Box, Group, Skeleton, Text } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import type { MRT_Cell, MRT_ColumnDef } from 'mantine-react-table';

import { IconChevronRight, IconEdit, IconPlus } from '@tabler/icons-react';
import type { TFunction } from 'i18next';

import { Badge } from '../../../../../shared/components/UI/Badge/Badge';
import { Tooltip } from '../../../../../shared/components/UI/Tooltip/Tooltip';
import { UserWithAvatar } from '../../../../../shared/components/UI/UserWithAvatar/UserWithAvatar';

import { useSidepanel } from '../../../../../shared/contexts/Sidepanel.provider';
import type { VaultPropertyAssignment } from '../../../../../shared/entities/BidResponse';
import type { VaultProperty } from '../../../../../shared/entities/VaultProperty';
import { formatDateInParisTZ } from '../../../../../shared/utils/dates';
import {
  CreatePropertySidePanelHeader,
  CreateVaultPropertySidePanel,
} from '../components/vaultPropertySidePanels/CreateVaultPropertySidePanel';
import {
  UpdatePropertySidePanelHeader,
  UpdateVaultPropertySidePanel,
} from '../components/vaultPropertySidePanels/UpdateVaultPropertySidePanel';
import {
  type BidResponseTableRow,
  isInferring,
} from '../hooks/bidResponse.hook';

export enum TableColumn {
  BID_RESPONSE = 'bidResponse',
  RESPONSE_DEADLINE = 'responseDeadline',
  OWNER = 'owner',
  ACTION = 'action',
}

const fixedWidthProps = (width?: number) => ({
  size: width,
  maxSize: width,
  minSize: width,
  mantineTableBodyCellProps: {
    sx: { flex: `0 0 ${width}px`, flexGrow: 0 },
  },
  mantineTableHeadCellProps: {
    sx: { flex: `0 0 ${width}px`, flexGrow: 0 },
  },
});

type I18nTFunction = TFunction<'bidResponse'>;
type ColumnType = (t: I18nTFunction) => MRT_ColumnDef<BidResponseTableRow>;

export const bidResponseTitle: ColumnType = t => ({
  header: t('vault.bidResponseTable.cells.bidResponse.header'),
  id: TableColumn.BID_RESPONSE,
  accessorFn: bidResponse => bidResponse.title,
  Cell: ({ cell }: { cell: MRT_Cell<BidResponseTableRow> }) => {
    const title = cell.getValue<string>();
    return (
      <Tooltip
        position="bottom-start"
        content={<div style={{ maxWidth: 600 }}>{title}</div>}
      >
        <Text truncate lineClamp={1}>
          {title}
        </Text>
      </Tooltip>
    );
  },
  ...fixedWidthProps(500),
});

export const responseDeadline: ColumnType = t => ({
  header: t('vault.bidResponseTable.cells.responseDeadline.header'),
  id: TableColumn.RESPONSE_DEADLINE,
  accessorFn: bidResponse => formatDateInParisTZ(bidResponse.responseDeadline),
  enableColumnFilter: false,
});

type OwnerCellData =
  | undefined
  | {
      avatarColor: string;
      firstName: string;
      lastName: string;
      disabledAt: Date | null;
    };
export const owner: ColumnType = t => ({
  header: t('vault.bidResponseTable.cells.owner.header'),
  id: TableColumn.OWNER,
  accessorFn: bidResponse => bidResponse.owner,
  Cell: ({ cell }: { cell: MRT_Cell<BidResponseTableRow> }) => {
    const owner = cell.getValue<OwnerCellData>();
    if (!owner) {
      return null;
    }
    return (
      <UserWithAvatar
        avatarColor={owner.avatarColor}
        firstName={owner.firstName}
        lastName={owner.lastName}
        isDisabled={!!owner.disabledAt}
      />
    );
  },
});

export const customProperty: (
  vaultProperty: VaultProperty,
) => MRT_ColumnDef<BidResponseTableRow> = vaultProperty => ({
  header: vaultProperty.name,
  Header: () => (
    <CustomPropertyHeader
      name={vaultProperty.name}
      vaultPropertyId={vaultProperty.id}
    />
  ),
  id: vaultProperty.name,
  accessorFn: bidResponse =>
    bidResponse.properties.find(
      property => property.propertyId === vaultProperty.id,
    ),
  Cell: ({ cell }: { cell: MRT_Cell<BidResponseTableRow> }) => {
    const property = cell.getValue<VaultPropertyAssignment | undefined>();

    if (property && property.values.length > 0) {
      return (
        <Group spacing="01" noWrap>
          {property.values.map(value => (
            <Badge
              key={value.name}
              bg={vaultProperty.color}
              c="gray.8"
              pr="02"
              sx={{ textTransform: 'none' }}
            >
              {value.name}
            </Badge>
          ))}
        </Group>
      );
    }

    if (isInferring(property?.ragStatus)) {
      return <Skeleton h={20} />;
    }

    return null;
  },
});

function CustomPropertyHeader({
  name,
  vaultPropertyId,
}: {
  name: string;
  vaultPropertyId: number;
}) {
  const { hovered, ref } = useHover();
  const { togglePanel, closePanel } = useSidepanel();

  const handleOpenUpdateVaultPropertySidePanel = () => {
    togglePanel({
      header: <UpdatePropertySidePanelHeader />,
      content: (
        <UpdateVaultPropertySidePanel
          vaultPropertyId={vaultPropertyId}
          onClose={closePanel}
        />
      ),
    });
  };

  return (
    <Group
      ref={ref}
      sx={{
        cursor: 'pointer',
      }}
      w="100%"
    >
      <Text>{name}</Text>
      {hovered ? (
        <ActionIcon size="xs" onClick={handleOpenUpdateVaultPropertySidePanel}>
          <IconEdit size={16} />
        </ActionIcon>
      ) : (
        <Box w="03" />
      )}
    </Group>
  );
}

export const action = {
  header: '',
  id: TableColumn.ACTION,
  Header: ActionHeader,
  Cell: ActionCell,
  ...fixedWidthProps(50),
};

function ActionHeader() {
  const { togglePanel, closePanel } = useSidepanel();

  return (
    <>
      <ActionIcon
        size="sm"
        onClick={() =>
          togglePanel({
            header: <CreatePropertySidePanelHeader />,
            content: <CreateVaultPropertySidePanel onClose={closePanel} />,
          })
        }
      >
        <IconPlus />
      </ActionIcon>
    </>
  );
}

function ActionCell() {
  return (
    <ActionIcon size="sm">
      <IconChevronRight />
    </ActionIcon>
  );
}
